import { useState } from 'react';

/* <OpenChartsLogo />
   ----------------------------------------------------------
   The OpenCharts brand mark. Three pieces, in order:

     1. Lime-green rounded-square icon — loaded from
        /assets/opencharts-icon.svg so the official artwork can
        be swapped in without touching the component. If the
        asset can't be loaded the inline SVG below renders in
        its place; the inline geometry mirrors the asset 1:1.
     2. "open"   — Instrument Serif italic, white.
     3. "charts" — Oxanium (Google Fonts) weight 700, lime
                   green, letter-spacing 1px (`hybrid-digital`).

   Pass `height` to scale the entire mark; the icon and the
   wordmark scale together.

   `iconOnly` renders just the lime icon (favicon, card pip).

   To use a higher-fidelity asset, drop the official file at
   public/assets/opencharts-icon.svg (or .avif and update
   ICON_SRC). The inline fallback is a vector approximation
   that ships with the component. */

const ICON_SRC = '/assets/opencharts-icon.svg';
const OPENCHARTS_GREEN = '#C8FF1A';
const OPENCHARTS_DARK = '#0A0A0A';

function InlineFallbackIcon({ size }) {
  /* Kept in sync with public/assets/opencharts-icon.svg. */
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <rect width="100" height="100" rx="20" fill={OPENCHARTS_GREEN} />
      <g fill={OPENCHARTS_DARK}>
        <rect x="44" y="6" width="12" height="14" rx="1.5" />
        <rect x="38" y="10" width="3" height="10" rx="0.6" />
        <rect x="59" y="10" width="3" height="10" rx="0.6" />
        <rect x="44" y="48" width="12" height="12" rx="1.5" />
        <rect x="42" y="78" width="16" height="12" rx="1.6" />
      </g>
      <g fill="none" stroke={OPENCHARTS_DARK} strokeLinecap="round" strokeLinejoin="round">
        <line x1="50" y1="20" x2="50" y2="26" strokeWidth="3.6" />
        <path d="M50 26 L78 42 L78 64 L50 80 L22 64 L22 42 Z" strokeWidth="4.6" />
        <path d="M50 26 L50 54" strokeWidth="4.6" />
        <path d="M50 54 L22 42" strokeWidth="4.6" />
        <path d="M50 54 L78 42" strokeWidth="4.6" />
        <g strokeWidth="2.6">
          <polyline points="32,36 36,40 40,36" />
          <polyline points="46,38 50,42 54,38" />
          <polyline points="60,36 64,40 68,36" />
        </g>
        <g strokeWidth="3.4">
          <rect x="28" y="42" width="12" height="20" rx="5" />
          <polyline points="36,60 36,72 30,72" />
        </g>
        <polyline points="70,44 62,44 62,64 70,64" strokeWidth="3.4" />
        <g strokeWidth="3">
          <line x1="50" y1="60" x2="50" y2="76" />
          <polyline points="47,73 50,76 53,73" />
        </g>
        <g strokeWidth="3">
          <line x1="58" y1="84" x2="74" y2="84" />
          <polyline points="71,81 74,84 71,87" />
        </g>
      </g>
    </svg>
  );
}

function Icon({ size, alt }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <InlineFallbackIcon size={size} />;
  return (
    <img
      src={ICON_SRC}
      alt={alt}
      width={size}
      height={size}
      onError={() => setFailed(true)}
      style={{ display: 'block', width: size, height: size, flexShrink: 0 }}
    />
  );
}

export default function OpenChartsLogo({
  height = 28,
  alt = 'OpenCharts',
  iconOnly = false,
  style,
  className,
  ...rest
}) {
  if (iconOnly) {
    return (
      <span
        role="img"
        aria-label={alt}
        className={className}
        style={{ display: 'inline-flex', lineHeight: 0, ...style }}
        {...rest}
      >
        <Icon size={height} alt={alt} />
      </span>
    );
  }

  /* Sizing — Oxanium’s cap height is taller than Instrument
     Serif italic at the same font-size, so we size "charts"
     a touch smaller to keep the two visually flush. */
  const openFontSize = Math.round(height * 0.82);
  const chartsFontSize = Math.round(height * 0.7);
  const gap = Math.max(6, Math.round(height * 0.26));

  return (
    <span
      role="img"
      aria-label={alt}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap,
        lineHeight: 1,
        ...style,
      }}
      {...rest}
    >
      <Icon size={height} alt="" />
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'baseline',
          lineHeight: 1,
        }}
      >
        <span
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontStyle: 'italic',
            fontWeight: 400,
            fontSize: openFontSize,
            color: 'var(--ink)',
            letterSpacing: '-0.01em',
            lineHeight: 1,
          }}
        >
          open
        </span>
        <span
          className="hybrid-digital"
          style={{
            /* hybrid-digital — Oxanium 700, geometric/digital feel */
            fontFamily: "'Oxanium', sans-serif",
            fontWeight: 700,
            fontSize: chartsFontSize,
            color: 'var(--charts)',
            letterSpacing: '1px',
            lineHeight: 1,
            marginLeft: Math.max(4, Math.round(height * 0.12)),
          }}
        >
          charts
        </span>
      </span>
    </span>
  );
}
